/**
 * After DOM mutations (e.g. replacing a logo block) the browser selection can
 * still reference detached nodes. Syncfusion RTE's `removeFormat` / formatting
 * commands then throw (`nodes[index] is null`). Collapse selection into the
 * live edit surface when it is missing or no longer connected.
 */
export function normalizeBrowserSelectionInRteEditPanel(
  editPanel: HTMLElement | null | undefined,
): void {
  if (!editPanel?.isConnected) return
  const doc = editPanel.ownerDocument
  const win = doc.defaultView ?? window
  const sel = win.getSelection?.()
  if (!sel) return

  const containsNode = (n: Node | null): boolean => {
    if (!n) return false
    try {
      const root: Node =
        n.nodeType === Node.TEXT_NODE ? ((n as Text).parentNode as Node) : n
      if (!root || root.nodeType === Node.DOCUMENT_NODE) return false
      return editPanel.contains(root as Node)
    } catch {
      return false
    }
  }

  let rangeOk = false
  try {
    if (sel.rangeCount > 0) {
      const r = sel.getRangeAt(0)
      const common = r.commonAncestorContainer
      const commonEl =
        common.nodeType === Node.TEXT_NODE
          ? ((common as Text).parentElement as Node | null)
          : (common as Node)
      rangeOk =
        containsNode(sel.anchorNode) &&
        containsNode(sel.focusNode) &&
        !!(commonEl && editPanel.contains(commonEl))
    }
  } catch {
    rangeOk = false
  }

  if (!rangeOk) {
    try {
      const range = doc.createRange()
      range.selectNodeContents(editPanel)
      range.collapse(false)
      sel.removeAllRanges()
      sel.addRange(range)
    } catch {
      /* ignore */
    }
  }
}
